import { useCallback, useEffect, useRef, useState } from 'react';
import useApiRequest from '../lib/hooks/useRequest';
import { Paginated } from '../lib/interfaces/utils';

export interface UseInfiniteListOptions {
  /** Builds the request url for a 1-based page number, e.g. `courses/admin?page=${page}&limit=20`. */
  buildUrl: (page: number) => string;
}

export interface UseInfiniteList<T> {
  items: T[];
  /** Local edits (reorder, optimistic delete) write here instead of refetching the whole list. */
  setItems: React.Dispatch<React.SetStateAction<T[]>>;
  /** True only for the first page — later pages keep the existing list visible. */
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  /** Total across all pages, from the server. */
  count: number;
  /** Attach to an element below the list; entering the viewport loads the next page. */
  sentinelRef: (node: HTMLElement | null) => void;
  /** Drops everything and re-fetches from page 1. */
  reload: () => Promise<void>;
}

/**
 * Endless list backed by the platform's `Paginated<T>` envelope.
 *
 * A sentinel element is observed rather than the scroll position: the list lives in the page flow
 * here, so there is no scroll container to attach a listener to, and the observer also fires when
 * the first page is short enough that no scrolling is possible at all.
 */
const useInfiniteList = <T>({ buildUrl }: UseInfiniteListOptions): UseInfiniteList<T> => {
  const { apiRequest } = useApiRequest();

  const [items, setItems] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [count, setCount] = useState(0);

  // Read inside the observer callback, which must not close over a stale page number.
  const pageRef = useRef(0);
  const isFetchingRef = useRef(false);
  // Bumped on every reload; an in-flight response from an older generation is discarded.
  const generationRef = useRef(0);

  const fetchPage = useCallback(
    async (page: number, { force = false }: { force?: boolean } = {}) => {
      if (isFetchingRef.current && !force) return;
      isFetchingRef.current = true;

      const generation = generationRef.current;

      const isFirst = page === 1;
      if (isFirst) setIsLoading(true);
      else setIsLoadingMore(true);

      try {
        const response = await apiRequest<Paginated<T>>(buildUrl(page), 'GET');
        if (!response.data) return;
        // A reload started while this was in flight — its result is the current truth, not ours.
        if (generation !== generationRef.current) return;

        const { docs, hasNextPage, count: total } = response.data;
        setItems((current) => (isFirst ? docs : [...current, ...docs]));
        setHasMore(hasNextPage);
        setCount(total);
        pageRef.current = page;
      } finally {
        // A superseded fetch must not clear flags the reload that replaced it has just set.
        if (generation === generationRef.current) {
          isFetchingRef.current = false;
          setIsLoading(false);
          setIsLoadingMore(false);
        }
      }
    },
    [apiRequest, buildUrl]
  );

  // Forced past the in-flight guard: a reload follows a create or delete, so the list on screen is
  // already known to be wrong and waiting for a page load to finish would only show it for longer.
  const reload = useCallback(async () => {
    pageRef.current = 0;
    generationRef.current += 1;
    await fetchPage(1, { force: true });
  }, [fetchPage]);

  useEffect(() => {
    fetchPage(1);
  }, [fetchPage]);

  const sentinelNodeRef = useRef<HTMLElement | null>(null);

  const sentinelRef = useCallback((node: HTMLElement | null) => {
    sentinelNodeRef.current = node;
  }, []);

  const canLoadMore = hasMore && !isLoading && !isLoadingMore;

  /**
   * Re-created whenever loading finishes rather than kept alive across pages. An observer only
   * reports *changes* in intersection — a sentinel that stays in view after a short page would
   * never fire again and the list would stall. Observing anew always yields one initial callback.
   */
  useEffect(() => {
    const node = sentinelNodeRef.current;
    if (!node || !canLoadMore) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) fetchPage(pageRef.current + 1);
    });
    observer.observe(node);

    return () => observer.disconnect();
  }, [canLoadMore, fetchPage]);

  return { items, setItems, isLoading, isLoadingMore, hasMore, count, sentinelRef, reload };
};

export default useInfiniteList;
